import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, User, Lock, KeyRound, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
    'Sénégal', 'Côte d\'Ivoire', 'Bénin', 'Togo', 'Burkina Faso', 'Mali', 'Niger', 'Guinée',
    'Cameroun', 'Gabon', 'Congo', 'RD Congo', 'Tchad', 'Centrafrique',
    'Maroc', 'Algérie', 'Tunisie', 'Mauritanie',
    'Madagascar', 'Comores', 'Maurice', 'Seychelles',
    'France', 'Belgique', 'Suisse', 'Luxembourg', 'Canada', 'Autre',
];

export default function Profile() {
    const { session, refreshProfile } = useAuth();
    const { setLang } = useLang();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        full_name: '', phone: '', job_function: '',
        country: '', city: '', observation: '', preferred_language: 'fr',
    });
    const [email, setEmail] = useState('');
    const [pwd, setPwd] = useState({ value: '', confirm: '', show: false });
    const [pwdBusy, setPwdBusy] = useState(false);

    useEffect(() => {
        if (!session?.access_token) return;
        (async () => {
            try {
                const r = await axios.get(`${API}/profile/me`, {
                    headers: { Authorization: `Bearer ${session.access_token}` },
                });
                const p = r.data || {};
                setEmail(p.email || '');
                setForm({
                    full_name: p.full_name || '',
                    phone: p.phone || '',
                    job_function: p.job_function || '',
                    country: p.country || '',
                    city: p.city || '',
                    observation: p.observation || '',
                    preferred_language: p.preferred_language || 'fr',
                });
            } catch (e) {
                toast.error(e.response?.data?.detail || e.message);
            }
            setLoading(false);
        })();
    }, [session?.access_token]);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.patch(`${API}/profile/me`, form, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            toast.success('Profil mis à jour');
            if (form.preferred_language) setLang(form.preferred_language);
            if (refreshProfile) refreshProfile();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setBusy(false);
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (pwd.value.length < 8) { toast.error('Mot de passe trop court (≥ 8 caractères)'); return; }
        if (pwd.value !== pwd.confirm) { toast.error('La confirmation ne correspond pas'); return; }
        setPwdBusy(true);
        try {
            await axios.post(`${API}/profile/change-password`, { new_password: pwd.value }, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            toast.success('Mot de passe modifié');
            setPwd({ value: '', confirm: '', show: false });
            supabase.auth.refreshSession().catch(() => {});
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setPwdBusy(false);
    };

    if (loading) {
        return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;
    }

    return (
        <div className="max-w-3xl mx-auto p-6 space-y-10" data-testid="page-profile">
            <header>
                <div className="eyebrow mb-2 text-primary">Mon compte</div>
                <h1 className="font-heading text-4xl">Profil &amp; sécurité</h1>
                <p className="text-foreground/65 mt-3 leading-relaxed">
                    Mettez à jour vos informations personnelles et votre mot de passe. Vos données ne sont visibles
                    que par les admins de vos espaces et par les Super Admins de la plateforme.
                </p>
            </header>

            <section className="card-soft p-7" data-testid="profile-info-card">
                <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-lg bg-primary-light text-primary flex items-center justify-center"><User size={18} strokeWidth={1.6} /></div>
                    <h2 className="font-heading text-2xl">Informations personnelles</h2>
                </div>
                <form onSubmit={save} className="space-y-5">
                    <div className="space-y-2">
                        <Label>Email</Label>
                        <Input value={email} disabled className="bg-muted/30" data-testid="profile-email" />
                        <p className="text-xs text-muted-foreground">L'email ne peut pas être modifié. Contactez votre admin si nécessaire.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="full_name">Nom complet</Label>
                            <Input id="full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="profile-fullname" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="job_function">Fonction</Label>
                            <Input id="job_function" value={form.job_function} onChange={(e) => setForm({ ...form, job_function: e.target.value })} placeholder="DAF, Pilote projet…" data-testid="profile-jobfunction" />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="phone">Téléphone</Label>
                            <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+33 6 12 34 56 78" data-testid="profile-phone" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="country">Pays</Label>
                            <select
                                id="country"
                                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                value={form.country}
                                onChange={(e) => setForm({ ...form, country: e.target.value })}
                                data-testid="profile-country"
                            >
                                <option value="">—</option>
                                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="city">Ville</Label>
                            <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="profile-city" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="observation">Observation</Label>
                        <Textarea
                            id="observation" rows={3}
                            value={form.observation}
                            onChange={(e) => setForm({ ...form, observation: e.target.value })}
                            maxLength={1000}
                            placeholder="Note libre — visible par les admins de vos espaces. Ex. : disponibilités, spécialités, langues parlées…"
                            data-testid="profile-observation"
                        />
                        <div className="text-xs text-muted-foreground text-right">{(form.observation || '').length}/1000</div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="lang">Langue préférée</Label>
                        <select
                            id="lang"
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            value={form.preferred_language}
                            onChange={(e) => setForm({ ...form, preferred_language: e.target.value })}
                            data-testid="profile-language"
                        >
                            <option value="fr">Français</option>
                            <option value="en">English</option>
                        </select>
                    </div>
                    <div className="flex justify-end">
                        <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="profile-save">
                            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                            Enregistrer
                        </Button>
                    </div>
                </form>
            </section>

            <section className="card-soft p-7" data-testid="profile-password-card">
                <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-lg bg-secondary-light text-secondary flex items-center justify-center"><Lock size={18} strokeWidth={1.6} /></div>
                    <h2 className="font-heading text-2xl">Mot de passe</h2>
                </div>
                <form onSubmit={changePassword} className="space-y-5 max-w-md">
                    <div className="space-y-2">
                        <Label htmlFor="pwd">Nouveau mot de passe</Label>
                        <div className="relative">
                            <Input
                                id="pwd" type={pwd.show ? 'text' : 'password'}
                                value={pwd.value}
                                onChange={(e) => setPwd({ ...pwd, value: e.target.value })}
                                minLength={8}
                                placeholder="≥ 8 caractères"
                                data-testid="profile-new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setPwd({ ...pwd, show: !pwd.show })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                aria-label={pwd.show ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            >
                                {pwd.show ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="pwd2">Confirmation</Label>
                        <Input
                            id="pwd2" type={pwd.show ? 'text' : 'password'}
                            value={pwd.confirm}
                            onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                            minLength={8}
                            data-testid="profile-confirm-password"
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button type="submit" disabled={pwdBusy || !pwd.value} variant="outline" data-testid="profile-change-password">
                            {pwdBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <KeyRound size={14} className="mr-2" />}
                            Modifier le mot de passe
                        </Button>
                    </div>
                </form>
            </section>
        </div>
    );
}
